import React from 'react';
import type { PromptDetails, ScoreDetailsV4, ScoreFileDetail } from '../../types';
import { formatDomainLabel, formatIntentLabel } from '../../utils/classificationLabels';
import { getScoreTier } from '../../utils/scoreTier';
import { ScoreBar } from './ScoreBar';
import { Fx, Group, K, O, R, V } from './Formula';
import {
  collectImprovementAreas,
  formatNum,
  formatRelevanceLabel,
  formatSkillCredit,
  formatSkillTerm,
  isScoreDetailsV4,
  judgeReasons,
  NOTHING_TO_CHANGE,
  penaltyCauseText,
  penaltyTitle,
  skillRows,
  TIER_DISPLAY,
  type ImprovementArea,
  type PenaltyCoachItem,
} from './explainScore';
import {
  BREAKDOWN_MAX,
  breakdownCaption,
  buildScoreBreakdown,
  formatFallValue,
  formatOverall,
  hasAnyRuleOrAgentAttachment,
  relevanceCountChips,
  relevanceScoreExplainer,
  rulesAndAgentsRows,
  tierCountChips,
  typeScoreExplainer,
} from './scoreBreakdown';

function MetricPair({ label, value }: { label: string; value: string }) {
  return (
    <div className="explain__metric">
      <span className="explain__eyebrow">{label}</span>
      <span>{value}</span>
    </div>
  );
}

function SectionTitle({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <h3 className="explain__title-row">
      <span className="explain__num" aria-hidden="true">{n}</span>
      {children}
    </h3>
  );
}

function CountChips({ chips }: { chips: { key: string; label: string; count: number; pillClass: string }[] }) {
  if (chips.length === 0) return null;
  return (
    <div className="explain__tier-row">
      {chips.map((chip) => (
        <span key={chip.key} className={`explain__tier-chip explain__tier-chip--${chip.key}`}>
          <span className="explain__tier-dot" />
          {chip.label}
          <b>{chip.count}</b>
        </span>
      ))}
    </div>
  );
}

function SkillCard({ file }: { file: ScoreFileDetail }) {
  const unidentified = file.tier_class === 'unidentified';
  const relevanceLabel = file.relevance_label?.trim().toLowerCase();
  const relevance = unidentified ? null : formatRelevanceLabel(file.relevance_label);
  return (
    <article className="explain__skill">
      <div className="explain__skill-head">
        <strong>{file.name || 'Untitled skill'}</strong>
        <span className={`pill pill--${file.tier_class}`}>
          {TIER_DISPLAY[file.tier_class] ?? file.tier_class}
        </span>
        {relevance && <span className={`pill pill--${relevanceLabel}`}>{relevance}</span>}
      </div>
      {file.relevance_reason && !unidentified && (
        <p className="explain__why">{file.relevance_reason}</p>
      )}
    </article>
  );
}

function ExplainV4({ v4 }: { v4: ScoreDetailsV4 }) {
  const reasons = judgeReasons(v4);
  const clarity = v4.scores?.task_clarity ?? null;
  const context = v4.scores?.prompt_context ?? null;
  const contextNotApplicable = v4.prompt_context_status === 'not_applicable';
  const contextCredit = contextNotApplicable
    ? (v4.derived?.prompt_context_credit ?? 10)
    : context;
  const base = v4.derived?.base_score ?? null;
  const typeScore = v4.derived?.type_score ?? null;
  const relevanceScore = v4.derived?.relevance_score ?? null;
  const skillCredit = v4.derived?.skill_score ?? null;
  const penalties = v4.derived?.penalties ?? [];
  const improvements = collectImprovementAreas(v4);
  const skills = skillRows(v4);

  const breakdown = buildScoreBreakdown(v4);
  const heroTier = getScoreTier(breakdown.finalScore);
  const typeChips = tierCountChips(skills);
  const relevanceChips = relevanceCountChips(skills);
  const ruleAgentRows = rulesAndAgentsRows(v4);
  const anyRuleOrAgentAttached = hasAnyRuleOrAgentAttachment(v4);

  return (
    <>
      <header className="explain__head">
        <h2>Score Breakdown and Explanation</h2>
        <p className="explain__faint">out of 10</p>
      </header>

      <section className="explain__fall" aria-label="How the score builds up">
        <span className="explain__eyebrow">How the score builds up</span>
        <ol className="explain__fall-rows">
          {breakdown.rows.map((row) => (
            <li key={row.id} className="explain__fall-row">
              <span className="explain__fall-label">{row.label}</span>
              {row.empty ? (
                <span className="explain__fall-empty">{row.emptyNote}</span>
              ) : (
                <span className="explain__fall-track">
                  <span
                    className={`explain__fall-bar explain__fall-bar--${row.id}`}
                    style={{
                      left: `${(Math.min(row.from, row.to) / BREAKDOWN_MAX) * 100}%`,
                      width: `${(Math.abs(row.to - row.from) / BREAKDOWN_MAX) * 100}%`,
                    }}
                  />
                </span>
              )}
              <span
                className={`explain__fall-val${
                  row.amount < 0 ? ' explain__fall-val--neg' : row.id !== 'base' && row.amount > 0 ? ' explain__fall-val--pos' : ''
                }`}
              >
                {row.empty ? '' : row.amount < 0 ? `−${formatFallValue(row.amount)}` : row.id === 'base' ? formatFallValue(row.amount) : `+${formatFallValue(row.amount)}`}
              </span>
            </li>
          ))}
          <li className="explain__fall-row explain__fall-row--overall">
            <span className="explain__fall-label">Overall</span>
            <span className="explain__fall-track">
              <span
                className="explain__fall-bar explain__fall-bar--overall"
                style={{
                  left: '0',
                  width: `${(breakdown.finalScore / BREAKDOWN_MAX) * 100}%`,
                  backgroundSize: `${breakdown.finalScore > 0 ? 10000 / ((breakdown.finalScore / BREAKDOWN_MAX) * 100) : 100}% 100%`,
                }}
              />
            </span>
            <span className={`explain__fall-val explain__fall-overall-val--${heroTier}`}>
              {formatOverall(breakdown.finalScore)}
            </span>
          </li>
        </ol>
        <div className="explain__fall-axis">
          <span />
          <span className="explain__fall-axis-ticks" aria-hidden="true">
            <span>0</span>
            <span>10</span>
          </span>
          <span />
        </div>
        <p className="explain__fall-note">{breakdownCaption(breakdown)}</p>
      </section>

      <section className="explain__section">
        <SectionTitle n={1}>About this prompt</SectionTitle>
        <div className="explain__metrics">
          <MetricPair label="Domain" value={formatDomainLabel(v4.domain_type)} />
          <MetricPair label="Intent" value={formatIntentLabel(v4.session_intent)} />
        </div>
      </section>

      <section className="explain__section">
        <SectionTitle n={2}>Base score</SectionTitle>
        <div className="explain__dim">
          <div className="explain__dim-row">
            <span>Task clarity</span>
            <span className="num">{formatNum(clarity, 1)} / 10</span>
          </div>
          <ScoreBar value={Number(clarity || 0)} max={10} label="Task clarity" />
          {reasons.tc && <p className="explain__why">{reasons.tc}</p>}
        </div>
        <div className="explain__dim">
          <div className="explain__dim-row">
            <span>Prompt context</span>
            <span className="num">
              {contextNotApplicable ? 'Not applicable' : `${formatNum(context, 1)} / 10`}
            </span>
          </div>
          {contextNotApplicable ? (
            <p className="explain__muted">This ask does not need a location or source material.</p>
          ) : (
            <ScoreBar value={Number(context || 0)} max={10} label="Prompt context" />
          )}
          {reasons.pc && <p className="explain__why">{reasons.pc}</p>}
        </div>
        <p className="explain__sum">
          <Fx>
            <Group>
              <K>0.4</K>
              <O>×</O>
              <V>{formatNum(clarity, 1)}</V>
            </Group>
            <O>+</O>
            <Group>
              <K>0.4</K>
              <O>×</O>
              <V>{formatNum(contextCredit, 1)}</V>
            </Group>
            <O>=</O>
            <R tone="base">{formatNum(base, 1)}</R>
            <span className="fx-label"> of 8</span>
            {contextNotApplicable ? <span className="fx-label"> (context not needed)</span> : null}
          </Fx>
        </p>
      </section>

      <section className="explain__section">
        <SectionTitle n={3}>Skills you attached</SectionTitle>
        {skills.length === 0 ? (
          <p className="explain__muted">No skills attached.</p>
        ) : (
          skills.map((file) => (
            <SkillCard key={file.id ?? file.name ?? Math.random()} file={file} />
          ))
        )}
        <div className="explain__cards">
          <article className="explain__mini">
            <span className="explain__eyebrow">Type score</span>
            <strong className="num">{formatSkillTerm(typeScore, 2)}</strong>
            <ScoreBar value={Number(typeScore || 0)} max={1} label="Type score" />
            <p className="explain__why">{typeScoreExplainer(skills)}</p>
            <CountChips chips={typeChips} />
          </article>
          <article className="explain__mini">
            <span className="explain__eyebrow">Relevance score</span>
            <strong className="num">{formatSkillTerm(relevanceScore, 2)}</strong>
            <ScoreBar value={Number(relevanceScore || 0)} max={1} label="Relevance score" />
            <p className="explain__why">{relevanceScoreExplainer(skills)}</p>
            <CountChips chips={relevanceChips} />
          </article>
        </div>
        <p className="explain__sum">
          <Fx>
            <Group>
              <span className="fx-label">Type </span>
              <V tone="type">{formatSkillTerm(typeScore, 2)}</V>
              <O>×</O>
              <span className="fx-label">relevance </span>
              <V tone="rel">{formatSkillTerm(relevanceScore, 2)}</V>
              <O>×</O>
              <K>2</K>
            </Group>
            <O>=</O>
            <R tone="credit">{formatSkillCredit(skillCredit)}</R>
            <span className="fx-label"> of 2</span>
          </Fx>
        </p>
      </section>

      <section className="explain__section">
        <SectionTitle n={4}>Rules and agents</SectionTitle>
        {ruleAgentRows.length === 0 ? (
          <p className="explain__muted">
            {anyRuleOrAgentAttached ? 'No bonus earned from the attached rules or agents.' : 'No rules or agents attached, so no bonus.'}
          </p>
        ) : (
          <>
            {ruleAgentRows.map((row) => (
              <div key={row.id} className="explain__rule-row">
                <span className="pill pill--teal">{row.label}</span>
                <span className="explain__rule-bonus">+{formatNum(row.amount, 1)}</span>
              </div>
            ))}
            <p className="explain__sum">
              <span>Bonus for attached rules and agents</span>
              {' → '}
              <b className="num">+{formatNum(breakdown.bonusTotal, 1)}</b>
            </p>
          </>
        )}
      </section>

      <section className="explain__section">
        <SectionTitle n={5}>Penalties</SectionTitle>
        {penalties.length === 0 ? (
          <p className="explain__muted">No penalties applied.</p>
        ) : (
          <ul className="explain__penalties">
            {penalties.map((penalty, index) => (
              <li key={`${penalty.cause}-${index}`}>
                <div>
                  <strong>{penaltyTitle(penalty)}</strong>
                  <p>{penaltyCauseText(penalty)}</p>
                </div>
                <span className="explain__penalty-amt">−{formatNum(penalty.amount, 1)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="explain__section">
        <SectionTitle n={6}>Overall score</SectionTitle>
        <div className={`explain__hero explain__hero--${heroTier}`}>
          <p className="explain__hero-formula">
            <Fx>
              <Group>
                <V tone="base">{formatNum(breakdown.base, 1)}</V>
                <span className="fx-label"> base</span>
              </Group>
              <O>+</O>
              <Group>
                <V tone="credit">{formatNum(breakdown.skillCredit, 2)}</V>
                <span className="fx-label"> skill credit</span>
              </Group>
              {breakdown.ruleBonus > 0 && (
                <>
                  <O>+</O>
                  <Group>
                    <V tone="bonus">{formatNum(breakdown.ruleBonus, 1)}</V>
                    <span className="fx-label"> rule</span>
                  </Group>
                </>
              )}
              {breakdown.agentBonus > 0 && (
                <>
                  <O>+</O>
                  <Group>
                    <V tone="bonus">{formatNum(breakdown.agentBonus, 1)}</V>
                    <span className="fx-label"> agent</span>
                  </Group>
                </>
              )}
              <O>−</O>
              <Group>
                <V tone="pen">{formatNum(breakdown.penaltyTotal, 1)}</V>
                <span className="fx-label"> penalties</span>
              </Group>
              <O>=</O>
              <R tone="overall">{formatNum(breakdown.rawTotal, 1)}</R>
              {breakdown.clamped ? <span className="fx-label">, capped at 10</span> : null}
            </Fx>
          </p>
          <span className={`explain__hero-num explain__hero-num--${heroTier}`}>
            {formatOverall(breakdown.finalScore)}
          </span>
        </div>
      </section>

      <ImprovementAreas areas={improvements} />
    </>
  );
}

function CoachIcon({ id }: { id: string }) {
  const props = {
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    'aria-hidden': true as const,
  };
  if (id === 'penalty') {
    return (
      <svg {...props}>
        <path d="M12 9v4M12 17h.01" />
        <path d="M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
      </svg>
    );
  }
  if (id === 'penalty-intent') {
    return (
      <svg {...props}>
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
      </svg>
    );
  }
  if (id === 'clarity') {
    return (
      <svg {...props}>
        <path d="M4 6h16M4 12h10M4 18h6" />
      </svg>
    );
  }
  if (id === 'context') {
    return (
      <svg {...props}>
        <path d="M3 12h4M17 12h4M12 3v4M12 17v4" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );
  }
  if (id === 'type') {
    return (
      <svg {...props}>
        <path d="M12 3l8 3v6c0 4.5-3.2 8.2-8 9-4.8-.8-8-4.5-8-9V6z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    );
  }
  return (
    <svg {...props}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function PenaltyBox({ item }: { item: PenaltyCoachItem }) {
  const pillClass =
    item.pill === 'default' ? 'pill' : `pill pill--${item.pill}`;
  const names = item.attachmentNames;
  const kindLabel = item.attachmentKind
    ? `${item.attachmentKind}${names.length > 1 ? 's' : ''}`
    : '';
  return (
    <div className="explain__pbox">
      <div className="explain__coach-head">
        <span className={pillClass}>{item.label}</span>
        {names.length > 0 && (
          <span className="explain__attach-meta">
            {kindLabel}:{' '}
            {names.map((name, index) => (
              <React.Fragment key={name}>
                {index > 0 ? ', ' : null}
                <span className="explain__mono">{name}</span>
              </React.Fragment>
            ))}
          </span>
        )}
      </div>
      {item.tone === 'plain' ? (
        <p>{item.body}</p>
      ) : (
        <p className={`explain__say${item.tone === 'warn' ? ' explain__say--warn' : ''}`}>{item.body}</p>
      )}
    </div>
  );
}

function CoachCard({ area }: { area: ImprovementArea }) {
  const filePenalty = area.id === 'penalty';
  const intentPenalty = area.id === 'penalty-intent';
  const toneClass = filePenalty ? ' explain__coach--penalty' : intentPenalty ? ' explain__coach--intent' : '';
  return (
    <article className={`explain__coach${toneClass}`}>
      <div className="explain__coach-head">
        <div className="explain__coach-title">
          <span className="explain__coach-icon">
            <CoachIcon id={area.id} />
          </span>
          <strong>{area.title}</strong>
        </div>
        {area.badge && (
          <span className={`pill${filePenalty ? ' explain__pill-fix' : ''}`}>{area.badge}</span>
        )}
      </div>
      <p>{area.lead}</p>
      {area.items?.map((item) => (
        <PenaltyBox key={item.id} item={item} />
      ))}
      {area.nextTime && area.nextTime.length > 0 && (
        <>
          <span className="explain__lab">Next time</span>
          <ul>
            {area.nextTime.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </>
      )}
    </article>
  );
}

function ImprovementAreas({ areas }: { areas: ImprovementArea[] }) {
  return (
    <section className="explain__improve">
      <div>
        <h2>Improvement areas</h2>
        {areas.length > 0 && (
          <p className="explain__muted">Where to put your effort on the next prompt, most important first.</p>
        )}
      </div>
      {areas.length === 0 ? (
        <article className="explain__coach">
          <div className="explain__coach-head">
            <strong>Nothing to change</strong>
          </div>
          <p>{NOTHING_TO_CHANGE}</p>
        </article>
      ) : (
        areas.map((area) => <CoachCard key={area.id} area={area} />)
      )}
    </section>
  );
}

export const ScorePanel: React.FC<{ prompt: PromptDetails }> = ({ prompt }) => {
  const details = prompt.score_details;
  if (!isScoreDetailsV4(details)) {
    return (
      <aside className="explain">
        <header className="explain__head">
          <h2>Score Breakdown and Explanation</h2>
        </header>
        <p className="explain__muted">Not available for this prompt.</p>
      </aside>
    );
  }
  return (
    <aside className="explain">
      <ExplainV4 v4={details} />
    </aside>
  );
};

export default ScorePanel;
